import React, { FC, useState } from 'react'
import Container from 'react-bootstrap/Container'
import isAuthenticated from '../../routes/PrivateRoute'
import PageTitle from '../../components/PageTitle'
import DataSharingGuard from '../../components/DataSharingGuard'
import SharingSection from './SharingSection'
import ConfirmationModal from './ConfirmationModal'
import SuggestHealthPlanModal from './SuggestHealthModal'

type SharingSectionType =
    | 'otherHealthPlans'
    | 'careProviders'

type SharingRow = {
    id: string
    displayName: string
    details: string
    firstRequest: string
    mostRecentAccess?: string
    status: 'Sharing' | 'Not Sharing'
}

type HealthPlanOptInType = 'previous' | 'current'

type PendingConfirmation =
    | { kind: 'providerOptIn' }
    | { kind: 'sectionOptOut'; section: SharingSectionType }
    | {
        kind: 'healthPlanOptIn'
        planName: string
        type: HealthPlanOptInType
        memberId: string
    }

const initialHealthPlans: SharingRow[] = []

const initialCareProviders: SharingRow[] = [
    {
        id: 'aurora-healthcare',
        displayName: 'Aurora Healthcare',
        details: 'Primary Care Provider: Jason Wells, MD',
        firstRequest: '04/12/2026',
        mostRecentAccess: '07/12/2026',
        status: 'Sharing',
    },
    {
        id: 'oak-forest-provider',
        displayName: 'Oak Forest Provider',
        details: 'Primary Care Provider: Jane Foster, MD',
        firstRequest: '01/12/2026',
        mostRecentAccess: '09/12/2026',
        status: 'Sharing',
    },
]

const DataSharing: FC = () => {
    const [healthPlanRows, setHealthPlanRows] =
        useState<SharingRow[]>(initialHealthPlans)

    const [providerRows, setProviderRows] =
        useState<SharingRow[]>(initialCareProviders)

    const [providerSharing, setProviderSharing] =
        useState(true)

    const [showSuggestPlanForm, setShowSuggestPlanForm] = useState(false)

    const [pendingConfirmation, setPendingConfirmation] =
        useState<PendingConfirmation | null>(null)

    const pendingSectionOptOut =
        pendingConfirmation?.kind === 'sectionOptOut'
            ? pendingConfirmation.section
            : null

    const pendingHealthPlanOptIn =
        pendingConfirmation?.kind === 'healthPlanOptIn'
            ? {
                planName: pendingConfirmation.planName,
                type: pendingConfirmation.type,
                memberId: pendingConfirmation.memberId,
            }
            : null

    const hasSharingHealthPlan = healthPlanRows.some(
        (row) => row.status === 'Sharing'
    )

    const confirmProviderOptIn = () => {
        setProviderSharing(true)

        setProviderRows((rows) =>
            rows.map((row) => ({
                ...row,
                status: 'Sharing',
                firstRequest: 'Request In Process',
                mostRecentAccess: undefined,
            }))
        )

        setPendingConfirmation(null)
    }

    const confirmStopSharing = () => {
        if (pendingSectionOptOut === 'otherHealthPlans') {
            setHealthPlanRows((rows) =>
                rows.map((row) => ({
                    ...row,
                    firstRequest:
                        row.firstRequest === 'Request In Process'
                            ? '—'
                            : row.firstRequest,
                    status: 'Not Sharing',
                }))
            )
        }

        if (pendingSectionOptOut === 'careProviders') {
            setProviderSharing(false)

            setProviderRows((rows) =>
                rows.map((row) => ({
                    ...row,
                    firstRequest:
                        row.firstRequest === 'Request In Process'
                            ? '—'
                            : row.firstRequest,
                    status: 'Not Sharing',
                }))
            )
        }

        setPendingConfirmation(null)
    }

    const providerOptInMessage =
        'Confirm that you want to start sharing data with ' +
        'network providers with whom you have a treatment ' +
        'relationship.'

    const confirmHealthPlanOptIn = () => {
        if (!pendingHealthPlanOptIn) return

        const { planName, type, memberId } = pendingHealthPlanOptIn

        setHealthPlanRows((rows) => [
            {
                id: `${planName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${type}`,
                displayName: planName,
                details:
                    type === 'previous'
                        ? `Previous Member ID: ${memberId}`
                        : `Current Member ID: ${memberId}`,
                firstRequest: 'Request In Process',
                status: 'Sharing',
            },
            ...rows,
        ])

        setPendingConfirmation(null)
    }

    const getHealthPlanOptInMessage = () => {
        if (!pendingHealthPlanOptIn) return ''

        const source =
            pendingHealthPlanOptIn.type === 'previous'
                ? 'your previous health plan'
                : 'a current health plan'

        const transferSchedule =
            pendingHealthPlanOptIn.type === 'previous'
                ? 'one-time'
                : 'quarterly'

        return (
            `You are consenting for us to obtain your data from ${source}, ` +
            `${pendingHealthPlanOptIn.planName}. This will be a ` +
            `${transferSchedule} data transfer into our systems. Once the ` +
            'data has been transferred, it will be accessible to you in ' +
            'the Space Health Member Portal or with other apps you have ' +
            'authorized.'
        )
    }

    const getStopSharingMessage = () => {
        const sharingType =
            pendingSectionOptOut === 'careProviders'
                ? 'Provider'
                : 'Health Plan'

        return (
            `You are requesting to Opt Out of ${sharingType} Data Sharing. ` +
            'This will not remove data already shared, but it will ' +
            'prevent future sharing.'
        )
    }

    const handleConfirmationConfirm = () => {
        if (pendingConfirmation?.kind === 'providerOptIn') {
            confirmProviderOptIn()
            return
        }

        if (pendingConfirmation?.kind === 'healthPlanOptIn') {
            confirmHealthPlanOptIn()
            return
        }

        if (pendingConfirmation?.kind === 'sectionOptOut') {
            confirmStopSharing()
        }
    }

    const confirmationTitle =
        pendingConfirmation?.kind === 'sectionOptOut'
            ? 'Confirm Opt Out'
            : 'Confirm Opt In'

    const confirmationText =
        pendingConfirmation?.kind === 'sectionOptOut'
            ? getStopSharingMessage()
            : pendingConfirmation?.kind === 'healthPlanOptIn'
                ? getHealthPlanOptInMessage()
                : providerOptInMessage

    const confirmationConfirmText =
        pendingConfirmation?.kind === 'sectionOptOut'
            ? 'Confirm Opt Out'
            : 'Confirm Opt In'

    return (
        <DataSharingGuard>
            <section className='content'>
                <Container>
                    <PageTitle displayName='Data Sharing' />

                    <h1 className='headerText h2 mb-0'>
                        Data Sharing
                    </h1>

                    <section className='dataContainer'>
                        <p className='mb-0'>
                            Better data contributes to better health
                            outcomes. You have the right to control and
                            manage who can access your data. This page
                            allows you to Opt In and Opt Out of data
                            sharing.
                        </p>

                        <SharingSection
                            key={healthPlanRows.length}
                            title='Data sharing with other health plans'
                            description={
                                <>
                                    U.S. Government regulations (<a href="https://www.cms.gov/newsroom/fact-sheets/cms-interoperability-prior-authorization-final-rule-cms-0057-f" target="_blank" rel="noreferrer">CMS Rule 0057</a>) requires your approval (Opt In) before obtaining data from other health plans that you have used in the past or are currently using.
                                </>
                            }
                            rows={healthPlanRows}
                            sectionAction={
                                hasSharingHealthPlan
                                    ? 'opt-out'
                                    : undefined
                            }
                            onSectionAction={() =>
                                setPendingConfirmation({
                                    kind: 'sectionOptOut',
                                    section: 'otherHealthPlans',
                                })
                            }
                            optInPlanOptions={[
                                'Select Health Plan',
                                'Pine Valley Health Plan',
                                'Maple Ridge Health Plan',
                                'Forest Peak Health Plan',
                                'Health Plan Name',
                            ]}
                            optInTypeOptions={[
                                'Select Type',
                                'Previous Health Plan',
                                'Current Health Plan',
                            ]}
                            onOptInSelect={(planName, type, memberId) =>
                                setPendingConfirmation({
                                    kind: 'healthPlanOptIn',
                                    planName,
                                    type,
                                    memberId,
                                })
                            }
                            showOptInForm
                            hideTableWhenEmpty
                            onSuggestHealthPlan={() => setShowSuggestPlanForm(true)}
                        />

                        <SharingSection
                            title='Data sharing with your care providers'
                            description={
                                <>
                                    U.S. Government regulations (<a href="https://www.cms.gov/newsroom/fact-sheets/cms-interoperability-prior-authorization-final-rule-cms-0057-f" target="_blank" rel="noreferrer">CMS Rule 0057</a>) requires us to share your data upon request from any network provider with whom you have a treatment relationship. You have the right to Opt Out and deny current and future sharing.
                                </>
                            }
                            rows={providerRows}
                            sectionAction={
                                providerSharing
                                    ? 'opt-out'
                                    : 'opt-in'
                            }
                            firstColumnLabel='Care Provider'
                            onSectionAction={(action) => {
                                if (action === 'opt-in') {
                                    setPendingConfirmation({
                                        kind: 'providerOptIn',
                                    })
                                } else {
                                    setPendingConfirmation({
                                        kind: 'sectionOptOut',
                                        section: 'careProviders',
                                    })
                                }
                            }}
                        />
                    </section>
                </Container>
            </section>

            <ConfirmationModal
                show={pendingConfirmation !== null}
                title={confirmationTitle}
                message={confirmationText}
                confirmText={confirmationConfirmText}
                onConfirm={handleConfirmationConfirm}
                onClose={() => setPendingConfirmation(null)}
            />
            <SuggestHealthPlanModal
                show={showSuggestPlanForm}
                onClose={() => setShowSuggestPlanForm(false)}
            />
        </DataSharingGuard>
    )
}

export default isAuthenticated(DataSharing)